/**
 * GoalsSection — Mission section body (DEV-1081 Chunk C; DEV-1075 Phase 7.1).
 *
 * Renamed conceptually to "Mission" in the Progress tab pairings to
 * align with the Mission KPI shape label. The file name stays as
 * GoalsSection because the section header copy still reads "Goals" —
 * the rename is a UX concept; the file/component name preserves the
 * historical identifier.
 *
 * DEV-1075 Phase 7.1 — write paths migrated off the deprecated
 * MiniProjectGoalsController endpoints onto the unified
 * /api/kpiconfigs endpoints. Goal definitions now live as polymorphic
 * kpi_config rows (with mini_project_guid set, scoring_mode='qualitative'),
 * not in mini_project_goals_archive. See:
 *   - src/Data/MiniProjectConfig/goalPrimitiveMapping.ts (primitive bridge)
 *   - src/Data/MiniProjectConfig/miniProjectGoalView.ts (derived view model)
 *
 * Template-lock regression (knowingly accepted in Phase 7.1):
 *   The pre-DEV-1075 code locked nameKey + targetValue on template goals
 *   (those with playbookGoalKey set). The Phase 3 backfill didn't carry
 *   playbook_goal_key over to kpi_config — there's no column for it. So
 *   the template lock UI is removed here. All goals appear editable.
 *   Re-introducing the lock needs either a schema column or a different
 *   tracking mechanism (e.g. mission_type_config.playbook_goal_key).
 *   Filed for post-Phase-7 follow-up before any production launch.
 *
 * Strings localized via `pages:miniProject.sections.*` (shared form
 * vocabulary) and `pages:miniProject.sections.goals.*` (goal-specific).
 * Chunk D Phase 2 sweep, 2026-05-22.
 */

import { useState } from "react";
import { useAppTranslation } from "../../../i18n/useAppTranslation";
import type { MiniProjectGoalView } from "../../../Data/MiniProjectConfig/miniProjectGoalView";
import type { MiniProjectAssessment } from "../../../api/miniProjectStatuses";
import MiniProjectGoalCard from "../../../Components/MiniProjectGoalCard/MiniProjectGoalCard";
import GoalEditTray from "../../../Components/MiniProjectGoalEdit/GoalEditTray";
import GoalDataEntryTray from "../../../Components/MiniProjectGoalEdit/GoalDataEntryTray";
import GoalDetailModal from "../../../Components/MiniProjectGoalDetailModal/GoalDetailModal";
import styles from "./sections.module.css";

interface Props {
  miniProjectGuid: string;
  goals: MiniProjectGoalView[];
  isLead: boolean;
  onMutated: () => void;
  /** Add-form open state, lifted to ProgressTab so the SectionPanel
   *  header "+" button can trigger it (DEV-1081 Chunk D). */
  addOpen?: boolean;
  onCloseAdd?: () => void;
  /**
   * Optional. Map of goalGuid (UPPER-cased) → latest filed-status
   * assessment for that goal. When provided, each card's progress bar
   * is colored by its assessment instead of the binary
   * complete-vs-in-progress default. Lookup is uppercase to avoid GUID
   * case mismatches (CLAUDE.md security convention). Consumed by the
   * Mission sub-tab (#230); other consumers (magazine spread) can
   * omit it.
   */
  latestAssessments?: Map<string, MiniProjectAssessment>;
}

export default function GoalsSection({
  miniProjectGuid,
  goals,
  isLead,
  onMutated,
  addOpen = false,
  onCloseAdd,
  latestAssessments,
}: Props) {
  const { t } = useAppTranslation("pages");
  /** GUID of the goal currently in full-edit mode. null when no tray is
   *  open. Only one can be open at a time — keeps the layout stable. */
  const [editingGoalGuid, setEditingGoalGuid] = useState<string | null>(null);
  const [dataEntryGoalGuid, setDataEntryGoalGuid] = useState<string | null>(null);
  const [viewingGoalGuid, setViewingGoalGuid] = useState<string | null>(null);

  // DEV-1075 Phase 7.3a — GoalEditTray is a side-panel Tray (portal),
  // rendered once at the section level rather than per-card. The
  // section stays static while the user configures one goal — they
  // can still see neighboring cards alongside, and the tray doesn't
  // push content around.
  //
  // Phase 7.3a-ii: two new section-level surfaces alongside the edit
  // tray — GoalDataEntryTray (Lead-only, database icon entry path)
  // and GoalDetailModal (public, body-click entry path). All three
  // are rendered once at the bottom; the card just dispatches via
  // onEdit / onDataEntry / onView.
  const editingGoal = editingGoalGuid
    ? goals.find((g) => g.goalGuid === editingGoalGuid) ?? null
    : null;
  const dataEntryGoal = dataEntryGoalGuid
    ? goals.find((g) => g.goalGuid === dataEntryGoalGuid) ?? null
    : null;
  const viewingGoal = viewingGoalGuid
    ? goals.find((g) => g.goalGuid === viewingGoalGuid) ?? null
    : null;

  if (goals.length === 0 && !isLead) {
    return <p className={styles.rowMeta}>{t("miniProject.sections.goals.empty")}</p>;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {goals.length === 0 && !addOpen ? (
        <p className={styles.rowMeta}>{t("miniProject.sections.goals.emptyAdding")}</p>
      ) : (
        goals.map((g) => (
          <MiniProjectGoalCard
            key={g.goalGuid}
            miniProjectGuid={miniProjectGuid}
            goal={g}
            isLead={isLead}
            onMutated={onMutated}
            onEdit={() => setEditingGoalGuid(g.goalGuid)}
            onDataEntry={() => setDataEntryGoalGuid(g.goalGuid)}
            onView={() => setViewingGoalGuid(g.goalGuid)}
            assessment={latestAssessments?.get(g.goalGuid.toUpperCase()) ?? null}
          />
        ))
      )}

      {/* Section-level edit tray. Keyed on editingGoalGuid so internal
          state resets when the Lead switches between goals (the new
          form is re-initialized from useState's initializer with the
          new goal's values). */}
      <GoalEditTray
        mode="edit"
        key={`edit-${editingGoalGuid ?? "closed"}`}
        open={editingGoalGuid !== null}
        goal={editingGoal}
        onClose={() => setEditingGoalGuid(null)}
        onMutated={() => {
          onMutated();
          setEditingGoalGuid(null);
        }}
      />

      {/* Section-level CREATE tray. Same component, mode="create". Skip
          2026-05-24: consistency with edit, and create flow gets all the
          new fields (description/photos/include_in_rollup/deadline) for
          free without a separate inline form. */}
      {isLead && (
        <GoalEditTray
          mode="create"
          key={`create-${addOpen ? "open" : "closed"}`}
          open={addOpen}
          miniProjectGuid={miniProjectGuid}
          existingMaxPosition={goals.reduce((m, g) => Math.max(m, g.position), -1)}
          onClose={() => onCloseAdd?.()}
          onMutated={() => {
            onMutated();
            onCloseAdd?.();
          }}
        />
      )}

      {/* Section-level data entry tray (Lead-only path via the
          database icon). Same key-on-guid pattern so state resets
          per goal switch. */}
      <GoalDataEntryTray
        key={`data-${dataEntryGoalGuid ?? "closed"}`}
        open={dataEntryGoalGuid !== null}
        goal={dataEntryGoal}
        onClose={() => setDataEntryGoalGuid(null)}
        onMutated={() => {
          onMutated();
          setDataEntryGoalGuid(null);
        }}
      />

      {/* Section-level read-only detail modal (any viewer, opens on
          card body click). Public-readable surface. */}
      <GoalDetailModal
        open={viewingGoalGuid !== null}
        goal={viewingGoal}
        onClose={() => setViewingGoalGuid(null)}
      />
    </div>
  );
}

/* ── Edit form lifted to GoalEditTray (DEV-1075 Phase 7.3a) ──
   The inline EditGoalForm that lived here pre-7.3a moved into
   src/Components/MiniProjectGoalEdit/GoalEditTray.tsx along with the
   four new fields (description, photos, include-in-rollup, deadline).
   See the file-level comment in GoalEditTray for the section layout
   and per-field disposition. */


/* ── Add form lifted to GoalEditTray (DEV-1075 Phase 7.3a-ii) ──
   The inline AddGoalForm that lived here pre-7.3a-ii moved into
   the same GoalEditTray component used for edit (mode="create").
   Per Skip 2026-05-24 — same surface for both flows so users
   learn one mental model + AddGoal gets the new fields
   (description, photos, include-in-rollup, deadline) automatically.
   See the file-level comment in GoalEditTray for the mode prop. */
